/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { getNullToZero } from '../Utils/EnergyUsagesViewDetailsUtils';
import {
  PERCENTAGE_HUNDRED,
  PROGRESS_GREY_PERCENTAGE,
  MAGIC_VALUE
} from '../Utils/Constants';

const commonProgressBarMax = 100;
const commonProgressBarMin = 0;
export const ternaryOpration = (
  argumentFirst: boolean | number | string,
  argumentSecond: number | string,
  // tslint:disable-next-line: no-any
  argumentThird: number | string
) => {
  if (argumentFirst) {
    return argumentSecond;
  } else {
    return argumentThird;
  }
};

export const progressBarCalculation = (
  actual: number | null,
  previousPeriod: number | null,
  savings: number | null
) => {
  const emptyActualAndPreviousdata =
    actual === commonProgressBarMin && previousPeriod === commonProgressBarMin;
  const greyLine = ternaryOpration(
    emptyActualAndPreviousdata,
    commonProgressBarMin,
    ternaryOpration(
      previousPeriod === 0,
      commonProgressBarMin,
      commonProgressBarMax
    )
  );

  const getPreviousPeriodAndActualRation = ternaryOpration(
    getNullToZero(savings) < commonProgressBarMin,
    getNullToZero(previousPeriod) / getNullToZero(actual),
    getNullToZero(actual) / getNullToZero(previousPeriod)
  ) as number;
  const percentBlue = ternaryOpration(
    previousPeriod !== commonProgressBarMin,
    getPreviousPeriodAndActualRation * commonProgressBarMax,
    greyLine
  );
  const baselineArrow = ternaryOpration(
    previousPeriod !== commonProgressBarMin,
    ternaryOpration(
      getNullToZero(savings) < commonProgressBarMin,
      percentBlue,
      commonProgressBarMax
    ),
    commonProgressBarMin
  );
  const percentRed = commonProgressBarMax - (percentBlue as number);
  return {
    percentBlue: percentBlue,
    baselineArrow: baselineArrow,
    percentRed: percentRed,
    emptyActualAndPreviousdata: emptyActualAndPreviousdata
  };
};

export const getPercentByColor = (
  percentBlue: number,
  percentRed: number,
  greyPecentage: number,
  actual: number | null,
  previousPeriod: number | null
) => {
  if (
    actual === null &&
    ((previousPeriod != null && previousPeriod >= 0) || previousPeriod === null)
  ) {
    return {
      updatedBlue: 0,
      updatedRed: 0,
      greyPecentage: 100
    };
  }
  if (previousPeriod === null && actual != null && actual >= 0) {
    return {
      updatedBlue: MAGIC_VALUE.HUNDRED - greyPecentage,
      updatedRed: 0,
      greyPecentage
    };
  }
  const remainigProgessBarPerc = PERCENTAGE_HUNDRED - greyPecentage;
  return {
    updatedBlue: (percentBlue * remainigProgessBarPerc) / MAGIC_VALUE.HUNDRED,
    updatedRed: (percentRed * remainigProgessBarPerc) / MAGIC_VALUE.HUNDRED,
    greyPecentage
  };
};
export const getBaselineArrowUpdated = (
  previousPeriod: number | null,
  actual: number | null,
  percentRed: number,
  greyPecentage: number
) => {
  if (actual === null && previousPeriod !== null && previousPeriod >= 0) {
    return PERCENTAGE_HUNDRED - PROGRESS_GREY_PERCENTAGE;
  } else if (actual === 0 && previousPeriod === 0) {
    return 0;
  } else if (
    previousPeriod !== null &&
    actual !== null &&
    previousPeriod - actual >= 0
  ) {
    return PERCENTAGE_HUNDRED - greyPecentage;
  } else {
    return PERCENTAGE_HUNDRED - greyPecentage - percentRed;
  }
};
export const getGreyLineWidth = (
  actual: number,
  previousPeriod: number,
  progessBarRelativeWidth: number | undefined | null
) => {
  const max = actual > previousPeriod ? actual : previousPeriod;
  if (progessBarRelativeWidth && max != progessBarRelativeWidth) {
    return (
      MAGIC_VALUE.HUNDRED -
      (max * MAGIC_VALUE.HUNDRED) / progessBarRelativeWidth
    );
  } else {
    return PROGRESS_GREY_PERCENTAGE;
  }
};
export const getUpdatedProgressBarWidth = (
  actual: number | null,
  previousPeriod: number | null,
  absoluteActual: number | null,
  absolutePrevPeriod: number | null,
  isProgressBarForEnergy: boolean | undefined,
  fromCarbonKPI?: boolean
) => {
  if (isProgressBarForEnergy && !fromCarbonKPI) {
    return {
      actualWidth: absoluteActual,
      prevWidth: absolutePrevPeriod
    };
  } else {
    return {
      actualWidth: actual,
      prevWidth: previousPeriod
    };
  }
};

export const getSavings = (
  savings: number | null,
  previousPeriod: number | null,
  actual: number | null
) => {
  return savings === null ? null : previousPeriod! - actual!;
};
